import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiErrorDetail, uploadImage, money } from "@/lib/api";
import { dateLocale } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Navbar, DashHeader, StatusBadge, PaymentBadge, Stars, VerifiedBadge, NotificationToggle } from "@/components/shared";
import { groupCategories } from "@/lib/categories";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Briefcase, Inbox, Send, AlertCircle, Upload, X, Loader2, ImagePlus, Navigation, ShieldCheck, IdCard, Wallet } from "lucide-react";

export default function ProDashboard() {
    const { t } = useTranslation();
    const { user, refresh } = useAuth();
    const [profile, setProfile] = useState(null);
    const [categories, setCategories] = useState([]);
    const [groups, setGroups] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [payments, setPayments] = useState({ items: [], totals: null });
    const [f, setF] = useState({ category: "", bio: "", hourly_rate: "", city: "", skills: "", photo_url: "", portfolio: [], experience_years: "", country: "IT", service_radius_km: "" });
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [uploadingPortfolio, setUploadingPortfolio] = useState(false);

    const load = useCallback(async () => {
        const [me, c, b, j, g, pay] = await Promise.all([
            api.get("/auth/me"),
            api.get("/categories"),
            api.get("/bookings"),
            api.get("/jobs"),
            api.get("/category-groups"),
            api.get("/pro/payments"),
        ]);
        setCategories(c.data);
        setBookings(b.data);
        setJobs(j.data);
        setGroups(g.data);
        setPayments(pay.data);
        const p = me.data.profile;
        setProfile(p);
        if (p) setF({
            category: p.category, bio: p.bio, hourly_rate: p.hourly_rate, city: p.city,
            skills: (p.skills || []).join(", "), photo_url: p.photo_url || "", portfolio: p.portfolio || [],
            experience_years: p.experience_years || "", country: p.country || "IT",
            service_radius_km: p.service_radius_km ?? "",
        });
    }, []);

    useEffect(() => { load(); }, [load]);

    const onPhoto = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingPhoto(true);
        try {
            const url = await uploadImage(file);
            setF((prev) => ({ ...prev, photo_url: url }));
            toast.success(t("Foto caricata"));
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
        finally { setUploadingPhoto(false); }
    };

    const onPortfolio = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploadingPortfolio(true);
        try {
            const urls = [];
            for (const file of files) urls.push(await uploadImage(file));
            setF((prev) => ({ ...prev, portfolio: [...prev.portfolio, ...urls] }));
            toast.success(t("{{count}} immagine/i aggiunta/e", { count: urls.length }));
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
        finally { setUploadingPortfolio(false); }
    };

    const removePortfolio = (i) => setF((prev) => ({ ...prev, portfolio: prev.portfolio.filter((_, idx) => idx !== i) }));

    const saveProfile = async () => {
        try {
            await api.post("/professionals/profile", {
                category: f.category, bio: f.bio, hourly_rate: parseFloat(f.hourly_rate), city: f.city,
                skills: f.skills.split(",").map((s) => s.trim()).filter(Boolean),
                photo_url: f.photo_url || null, portfolio: f.portfolio, experience_years: parseInt(f.experience_years) || 0,
                country: f.country, service_radius_km: f.service_radius_km === "" ? null : parseInt(f.service_radius_km),
            });
            toast.success(t("Profilo aggiornato!"));
            await refresh();
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const setStatus = async (id, status) => {
        try {
            await api.post(`/bookings/${id}/status`, { status });
            toast.success(t("Stato aggiornato"));
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div className="min-h-screen bg-cream">
            <Navbar />
            <div className="mx-auto max-w-6xl px-5 sm:px-8 py-10">
                <DashHeader
                    title={t("Dashboard professionista")}
                    subtitle={profile ? t("Gestisci lavori, offerte e profilo.") : t("Completa il profilo per iniziare a ricevere richieste.")}
                    action={
                        <div className="flex flex-wrap items-center gap-2">
                            {profile && <ProVerifyPill status={profile.verification_status || (profile.verified ? "verified" : "none")} />}
                            <NotificationToggle />
                        </div>
                    }
                />

                {!profile && (
                    <div className="mb-6 rounded-2xl border border-terracotta/30 bg-terracotta/5 p-4 flex items-center gap-3">
                        <AlertCircle className="h-5 w-5 text-terracotta" />
                        <p className="text-sm text-ink">{t("Non hai ancora un profilo pubblico. Compilalo nella scheda")} <b>{t("Profilo")}</b>.</p>
                    </div>
                )}

                <Tabs defaultValue={profile ? "bookings" : "profile"}>
                    <TabsList className="bg-creamalt w-full justify-start overflow-x-auto">
                        <TabsTrigger value="bookings" data-testid="tab-pro-bookings">{t("Lavori")}</TabsTrigger>
                        <TabsTrigger value="payments" data-testid="tab-pro-payments">{t("Incassi")}</TabsTrigger>
                        <TabsTrigger value="feed" data-testid="tab-pro-feed">{t("Richieste aperte")}</TabsTrigger>
                        <TabsTrigger value="profile" data-testid="tab-pro-profile">{t("Profilo")}</TabsTrigger>
                    </TabsList>

                    {/* BOOKINGS */}
                    <TabsContent value="bookings" className="mt-6">
                        {bookings.length === 0 ? (
                            <Empty icon={Briefcase} text={t("Nessun lavoro ancora. Fai offerte sulle richieste aperte!")} />
                        ) : (
                            <div className="grid gap-4">
                                {bookings.map((b) => (
                                    <div key={b.id} className="rounded-2xl border border-border bg-white p-5" data-testid={`pro-booking-${b.id}`}>
                                        <div className="flex flex-wrap items-start justify-between gap-4">
                                            <div>
                                                <p className="font-heading font-bold text-ink">{b.description}</p>
                                                <p className="text-sm text-terracotta">{b.category_name}</p>
                                                <p className="text-sm text-ink/60 mt-1">{t("Cliente: {{name}}", { name: b.client_name })}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-heading text-xl font-bold text-ink">{money(b.price, b.professional?.currency_symbol)}</p>
                                                {b.pricing && <p className="text-xs text-ink/70">{t("Netto (dopo {{pct}}%)", { pct: Math.round((b.pricing.commission_rate || 0.15) * 100) })}: {money(b.pricing.pro_net, b.professional?.currency_symbol)}</p>}
                                                <div className="mt-2 flex gap-2 justify-end"><StatusBadge status={b.status} /><PaymentBadge status={b.payment_status} /></div>
                                            </div>
                                        </div>
                                        <div className="mt-4 flex flex-col sm:flex-row flex-wrap gap-2 border-t border-border pt-4">
                                            {b.status === "confirmed" && b.payment_status !== "unpaid" && (
                                                <Button size="sm" className="w-full sm:w-auto rounded-full bg-ink hover:bg-inkhover" onClick={() => setStatus(b.id, "in_progress")} data-testid={`start-${b.id}`}>{t("Inizia lavoro")}</Button>
                                            )}
                                            {b.status === "in_progress" && (
                                                <Button size="sm" className="w-full sm:w-auto rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => setStatus(b.id, "completed")} data-testid={`complete-${b.id}`}>{t("Segna completato")}</Button>
                                            )}
                                            {b.status === "confirmed" && b.payment_status === "unpaid" && <p className="text-sm text-ink/50">{t("In attesa del pagamento del cliente…")}</p>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* PAYMENTS / INCASSI */}
                    <TabsContent value="payments" className="mt-6" data-testid="pro-payments">
                        {payments.totals && (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                                <SummaryCard label={t("Incassato (lordo)")} value={money(payments.totals.gross, payments.totals.currency_symbol)} testid="pay-gross" />
                                <SummaryCard label={t("Commissione ({{pct}}%)", { pct: Math.round((payments.totals.commission_rate || 0.15) * 100) })} value={`− ${money(payments.totals.commission, payments.totals.currency_symbol)}`} testid="pay-commission" />
                                <SummaryCard label={t("Netto per te")} value={money(payments.totals.net, payments.totals.currency_symbol)} highlight testid="pay-net" />
                            </div>
                        )}
                        {payments.items.length === 0 ? (
                            <Empty icon={Wallet} text={t("Ancora nessun incasso. Qui vedrai acconti e saldi ricevuti dai tuoi lavori.")} />
                        ) : (
                            <div className="rounded-2xl border border-border bg-white divide-y divide-border">
                                {payments.items.map((p) => (
                                    <div key={p.id} className="flex items-center justify-between gap-3 p-4" data-testid={`pay-item-${p.id}`}>
                                        <div className="min-w-0">
                                            <p className="font-medium text-ink truncate">{p.description || t("Prenotazione")}</p>
                                            <p className="text-xs text-ink/60">
                                                {p.client_name ? `${t("Cliente: {{name}}", { name: p.client_name })} · ` : ""}
                                                {p.phase === "deposit" ? t("Acconto") : t("Saldo")}
                                                {p.date ? ` · ${new Date(p.date).toLocaleDateString(dateLocale())}` : ""}
                                            </p>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <p className={`font-heading font-bold ${p.status === "refunded" ? "text-ink/40 line-through" : "text-ink"}`}>{money(p.amount, payments.totals?.currency_symbol)}</p>
                                            <PaymentBadge status={p.status} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* FEED */}
                    <TabsContent value="feed" className="mt-6">
                        {jobs.length === 0 ? (
                            <Empty icon={Inbox} text={t("Nessuna richiesta aperta al momento.")} />
                        ) : (
                            <div className="grid gap-4">
                                {jobs.map((j) => (
                                    <div key={j.id} className="rounded-2xl border border-border bg-white p-5" data-testid={`feed-job-${j.id}`}>
                                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                                        <div>
                                            <p className="font-heading font-bold text-ink">{j.title}</p>
                                            <p className="text-sm text-terracotta">{j.category_name} · {j.city}</p>
                                            {j.distance_km != null && (
                                                <p className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-emerald-700" data-testid={`job-distance-${j.id}`}>
                                                    <Navigation className="h-3 w-3" /> {j.distance_km < 1 ? t("a meno di 1 km da te") : t("a {{km}} km da te", { km: j.distance_km })}
                                                </p>
                                            )}
                                            {j.within_radius === false && (
                                                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 text-[11px] font-medium" data-testid={`out-of-zone-${j.id}`}>
                                                    {t("Fuori dalla tua zona")}
                                                </span>
                                            )}
                                            <p className="text-sm text-ink/60 mt-1">{j.description}</p>
                                            <p className="text-xs text-ink/40 mt-1">{t("Cliente: {{name}} · {{count}} offerte", { name: j.client_name, count: j.offers_count })}</p>
                                        </div>
                                        <div className="sm:text-right sm:shrink-0">
                                            {j.budget && <p className="font-heading font-bold text-ink">€{j.budget}</p>}
                                            <OfferDialog job={j} disabled={!profile} currencySymbol={profile?.currency_symbol || "€"} onDone={load} />
                                        </div>
                                    </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* PROFILE */}
                    <TabsContent value="profile" className="mt-6">
                        {profile && (
                            <div className="max-w-2xl mb-6">
                                <VerificationCard profile={profile} onDone={async () => { await refresh(); load(); }} />
                            </div>
                        )}
                        <div className="rounded-2xl border border-border bg-white p-6 max-w-2xl">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <Label>{t("Categoria")}</Label>
                                    <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                                        <SelectTrigger className="mt-1.5" data-testid="profile-category"><SelectValue placeholder={t("Scegli")} /></SelectTrigger>
                                        <SelectContent className="max-h-72">
                                            {groupCategories(categories, groups).map((g) => (
                                                <SelectGroup key={g.slug}>
                                                    <SelectLabel>{g.name}</SelectLabel>
                                                    {g.items.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                                                </SelectGroup>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div><Label>{t("Città")}</Label><Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="mt-1.5" data-testid="profile-city" /></div>
                                <div><Label>{t("Tariffa oraria")}</Label><Input type="number" value={f.hourly_rate} onChange={(e) => setF({ ...f, hourly_rate: e.target.value })} className="mt-1.5" data-testid="profile-rate" /></div>
                                <div><Label>{t("Anni di esperienza")}</Label><Input type="number" value={f.experience_years} onChange={(e) => setF({ ...f, experience_years: e.target.value })} className="mt-1.5" data-testid="profile-exp" /></div>
                                <div>
                                    <Label>{t("Paese (valuta)")}</Label>
                                    <Select value={f.country} onValueChange={(v) => setF({ ...f, country: v })}>
                                        <SelectTrigger className="mt-1.5" data-testid="profile-country"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="IT">{t("🇮🇹 Italia (€ EUR)")}</SelectItem>
                                            <SelectItem value="CH">{t("🇨🇭 Svizzera (CHF)")}</SelectItem>
                                            <SelectItem value="FR">{t("🇫🇷 Francia (€ EUR)")}</SelectItem>
                                            <SelectItem value="DE">{t("🇩🇪 Germania (€ EUR)")}</SelectItem>
                                            <SelectItem value="ES">{t("🇪🇸 Spagna (€ EUR)")}</SelectItem>
                                            <SelectItem value="GB">{t("🇬🇧 Regno Unito (£ GBP)")}</SelectItem>
                                            <SelectItem value="US">{t("🇺🇸 Stati Uniti ($ USD)")}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div><Label>{t("Zona di lavoro — raggio (km)")}</Label><Input type="number" value={f.service_radius_km} onChange={(e) => setF({ ...f, service_radius_km: e.target.value })} className="mt-1.5" placeholder={t("Es. 30")} data-testid="profile-radius" /></div>
                            </div>
                            <div className="mt-4">
                                <Label>{t("Foto profilo")}</Label>
                                <div className="mt-1.5 flex items-center gap-4">
                                    <div className="h-20 w-20 rounded-2xl overflow-hidden border border-border bg-creamalt flex items-center justify-center shrink-0">
                                        {f.photo_url ? <img src={f.photo_url} alt="" className="h-full w-full object-cover" /> : <ImagePlus className="h-6 w-6 text-ink/30" />}
                                    </div>
                                    <label className="cursor-pointer inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-medium text-ink hover:border-ink/30 transition-colors" data-testid="upload-photo-label">
                                        {uploadingPhoto ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                        {uploadingPhoto ? t("Caricamento…") : t("Carica foto")}
                                        <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="upload-photo-input" />
                                    </label>
                                </div>
                            </div>
                            <div className="mt-4">
                                <Label>{t("Portfolio lavori")}</Label>
                                <div className="mt-1.5 grid grid-cols-3 sm:grid-cols-4 gap-3">
                                    {f.portfolio.map((url, i) => (
                                        <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-border group" data-testid={`portfolio-item-${i}`}>
                                            <img src={url} alt="" className="h-full w-full object-cover" />
                                            <button onClick={() => removePortfolio(i)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors" data-testid={`remove-portfolio-${i}`}>
                                                <X className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    ))}
                                    <label className="cursor-pointer aspect-square rounded-xl border border-dashed border-border bg-creamalt flex flex-col items-center justify-center text-ink/40 hover:border-terracotta hover:text-terracotta transition-colors" data-testid="upload-portfolio-label">
                                        {uploadingPortfolio ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                                        <span className="text-xs mt-1">{t("Aggiungi")}</span>
                                        <input type="file" accept="image/*" multiple className="hidden" onChange={onPortfolio} data-testid="upload-portfolio-input" />
                                    </label>
                                </div>
                            </div>
                            <div className="mt-4"><Label>{t("Presentazione")}</Label><Textarea value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} className="mt-1.5" data-testid="profile-bio" /></div>
                            <div className="mt-4"><Label>{t("Competenze (separate da virgola)")}</Label><Input value={f.skills} onChange={(e) => setF({ ...f, skills: e.target.value })} className="mt-1.5" placeholder={t("Riparazioni, Emergenze 24h…")} data-testid="profile-skills" /></div>
                            <Button onClick={saveProfile} disabled={!f.category || !f.bio || !f.hourly_rate || !f.city} className="mt-6 rounded-full bg-terracotta hover:bg-terracottahover" data-testid="save-profile">
                                {profile ? t("Salva modifiche") : t("Crea profilo")}
                            </Button>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

function Empty({ icon: Icon, text }) {
    return (
        <div className="rounded-2xl border border-dashed border-border bg-white py-16 text-center text-ink/50">
            <Icon className="h-10 w-10 mx-auto mb-3" />
            <p>{text}</p>
        </div>
    );
}

function SummaryCard({ label, value, highlight, testid }) {
    return (
        <div className={`rounded-2xl border p-5 ${highlight ? "border-terracotta/30 bg-terracotta/5" : "border-border bg-white"}`} data-testid={testid}>
            <p className="text-xs text-ink/60">{label}</p>
            <p className={`mt-1 font-heading text-2xl font-bold ${highlight ? "text-terracotta" : "text-ink"}`}>{value}</p>
        </div>
    );
}

function ProVerifyPill({ status }) {
    const { t } = useTranslation();
    if (status === "verified") return <VerifiedBadge />;
    const map = {
        pending: { cls: "bg-amber-50 text-amber-800 border-amber-200", label: t("Documento in revisione"), Icon: Loader2, spin: true },
        rejected: { cls: "bg-rose-50 text-rose-800 border-rose-200", label: t("Verifica rifiutata"), Icon: AlertCircle },
        none: { cls: "bg-ink/5 text-ink/60 border-border", label: t("Non verificato"), Icon: ShieldCheck },
    };
    const s = map[status] || map.none;
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${s.cls}`} data-testid="pro-verify-pill">
            <s.Icon className={`h-3.5 w-3.5 ${s.spin ? "animate-spin" : ""}`} /> {s.label}
        </span>
    );
}

function VerificationCard({ profile, onDone }) {
    const { t } = useTranslation();
    const [uploading, setUploading] = useState(false);
    const vs = profile.verification_status || (profile.verified ? "verified" : "none");

    const onDoc = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const url = await uploadImage(file);
            await api.post("/professionals/verification", { document_url: url });
            toast.success(t("Documento inviato! Verificheremo la tua identità a breve."));
            await onDone();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
        finally { setUploading(false); e.target.value = ""; }
    };

    if (vs === "verified") {
        return (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 flex items-start gap-3" data-testid="verification-card">
                <ShieldCheck className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
                <div>
                    <p className="font-heading font-bold text-emerald-900">{t("Identità verificata")}</p>
                    <p className="text-sm text-emerald-800/80">{t("Il badge “Verificato” è attivo sul tuo profilo pubblico.")}</p>
                </div>
            </div>
        );
    }
    if (vs === "pending") {
        return (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-3" data-testid="verification-card">
                <Loader2 className="h-5 w-5 text-amber-700 shrink-0 mt-0.5 animate-spin" />
                <div>
                    <p className="font-heading font-bold text-amber-900">{t("Documento in revisione")}</p>
                    <p className="text-sm text-amber-800/80">{t("Abbiamo ricevuto il tuo documento. Ti avviseremo appena la verifica sarà completata.")}</p>
                </div>
            </div>
        );
    }
    return (
        <div className="rounded-2xl border border-border bg-white p-5" data-testid="verification-card">
            <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-terracotta/10 flex items-center justify-center shrink-0"><IdCard className="h-5 w-5 text-terracotta" /></div>
                <div className="flex-1">
                    <p className="font-heading font-bold text-ink">{t("Verifica la tua identità")}</p>
                    <p className="text-sm text-ink/60 mt-0.5">{t("Carica un documento d’identità (carta d’identità, patente o passaporto) per ottenere il badge")} <b>{t("Verificato")}</b> {t("e ricevere più richieste.")}</p>
                    {vs === "rejected" && (
                        <p className="mt-2 text-sm rounded-lg bg-rose-50 border border-rose-200 text-rose-800 px-3 py-2" data-testid="verification-rejected-note">
                            {t("Verifica rifiutata")}: {profile.verification_note || t("documento non valido. Ricarica un documento leggibile.")}
                        </p>
                    )}
                    <label className="mt-3 cursor-pointer inline-flex items-center gap-2 rounded-full bg-terracotta hover:bg-terracottahover text-white px-5 py-2 text-sm font-medium transition-colors" data-testid="upload-id-label">
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        {uploading ? t("Caricamento…") : vs === "rejected" ? t("Ricarica documento") : t("Carica documento")}
                        <input type="file" accept="image/*" className="hidden" onChange={onDoc} data-testid="upload-id-input" />
                    </label>
                    <p className="mt-2 text-[11px] text-ink/40">{t("Il documento è visibile solo al nostro team di verifica.")}</p>
                </div>
            </div>
        </div>
    );
}

function OfferDialog({ job, disabled, currencySymbol = "€", onDone }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [price, setPrice] = useState(job.budget || "");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const submit = async () => {
        setLoading(true);
        try {
            await api.post("/offers", { job_id: job.id, price: parseFloat(price), message });
            toast.success(t("Offerta inviata!"));
            setOpen(false);
            onDone();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setLoading(false); }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button size="sm" disabled={disabled} className="mt-2 w-full sm:w-auto rounded-full bg-ink hover:bg-inkhover" data-testid={`offer-btn-${job.id}`}><Send className="h-4 w-4 mr-1" /> {t("Fai offerta")}</Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading text-2xl">{t("Offerta per: {{title}}", { title: job.title })}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div><Label>{t("Il tuo prezzo ({{sym}})", { sym: currencySymbol })}</Label><Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1.5" data-testid="offer-price" /></div>
                    <div><Label>{t("Messaggio")}</Label><Textarea value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1.5" placeholder={t("Presentati e spiega come puoi aiutare…")} data-testid="offer-message" /></div>
                </div>
                <DialogFooter>
                    <Button onClick={submit} disabled={loading || !price || !message} className="w-full rounded-full bg-terracotta hover:bg-terracottahover" data-testid="submit-offer">{t("Invia offerta")}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
